import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import InfiniteScroll from 'react-infinite-scroll-component';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import TicketCard from '../components/TicketCard';
import { SOCCER_CLUBS } from '../constants/soccerData';
import firebaseService from '../services/firebaseService';
import './GameHistory.css';

const GameHistory = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedClub, setSelectedClub] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [updatingCards, setUpdatingCards] = useState(new Set());
  const [activeTab, setActiveTab] = useState('high-risk'); // 'high-risk' or 'live'
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [clubSearchTerm, setClubSearchTerm] = useState('');
  const [dateMode, setDateMode] = useState('single'); // 'single' | 'range' | 'month'
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [calendarTarget, setCalendarTarget] = useState('single'); // 'single'|'from'|'to'
  const [selectedMonthFilter, setSelectedMonthFilter] = useState(null);
  const [monthNavDate, setMonthNavDate] = useState(new Date());

  // Georgian month names
  const georgianMonths = [
    'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
    'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'
  ];

  // Georgian weekday names
  const georgianWeekdays = ['ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი'];

  const formatGeorgianDate = (date) => {
    const day = date.getDate();
    const month = georgianMonths[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };



  const allClubs = Object.values(SOCCER_CLUBS).flat();
  const clubs = ['ყველა კლუბი', ...allClubs];

  // Filter clubs based on search term
  const filteredClubs = clubs.filter(club =>
    club.toLowerCase().includes(clubSearchTerm.toLowerCase())
  );

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const setupSubscription = async () => {
      try {
        // Set up real-time listener for automatic updates
        unsubscribe = await firebaseService.subscribeToMatches((updatedMatches) => {
          if (!isMounted) return;

          console.log('GameHistory - Received matches:', updatedMatches);

          // Filter: Only show High Risk (formerly Fantasy), Regular and Live finished matches
          let historyMatches = updatedMatches.filter(match =>
            (match.status === 'win' || match.status === 'loss' || match.status === 'finished') &&
            (match.category === 'fantasy' || match.category === 'high-risk' || match.category === 'live' || match.category === 'regular')
          );

          console.log('GameHistory - Filtered matches:', historyMatches);

          setTickets(historyMatches);

          // Track which cards are being updated
          const updatingIds = new Set();
          historyMatches.forEach(newMatch => {
            const oldMatch = tickets.find(m => m.id === newMatch.id);
            if (oldMatch && JSON.stringify(oldMatch) !== JSON.stringify(newMatch)) {
              updatingIds.add(newMatch.id);
            }
          });

          if (updatingIds.size > 0) {
            setUpdatingCards(updatingIds);
            // Clear the updating state after animation
            setTimeout(() => {
              if (isMounted) setUpdatingCards(new Set());
            }, 1000);
          }
        });
      } catch (error) {
        console.error('Error setting up subscription:', error);
      }
    };

    const loadInitialData = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        // Load real matches from Firebase
        const matches = await firebaseService.getAllMatches();
        console.log('GameHistory - getAllMatches returned:', matches);
        if (!isMounted) return;

        // Filter: Only show High Risk (formerly Fantasy), Regular and Live finished matches
        let historyMatches = matches.filter(match =>
          (match.status === 'win' || match.status === 'loss' || match.status === 'finished') &&
          (match.category === 'fantasy' || match.category === 'high-risk' || match.category === 'live' || match.category === 'regular')
        );

        console.log('GameHistory - Setting tickets:', historyMatches);
        setTickets(historyMatches);
      } catch (error) {
        console.error('Error loading match history:', error);
        if (!isMounted) return;
        setTickets([]);
      }

      if (isMounted) setLoading(false);
    };

    loadInitialData();
    setupSubscription();

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Filter tickets based on selected date, club, and active tab
  useEffect(() => {
    let filtered = tickets.filter(ticket => {
      if (activeTab === 'live') {
        return ticket.category === 'live';
      } else if (activeTab === 'regular') {
        return ticket.category === 'regular';
      } else {
        // Standard history (High Risk + old Fantasy)
        return ticket.category === 'high-risk' || ticket.category === 'fantasy';
      }
    });

    if (dateMode === 'single' && selectedDate) {
      filtered = filtered.filter(ticket => {
        if (!ticket.date) return false;
        return ticket.date === format(selectedDate, 'yyyy-MM-dd');
      });
    } else if (dateMode === 'range' && dateFrom) {
      filtered = filtered.filter(ticket => {
        if (!ticket.date) return false;
        const fromStr = format(dateFrom, 'yyyy-MM-dd');
        const toStr = dateTo ? format(dateTo, 'yyyy-MM-dd') : fromStr;
        return ticket.date >= fromStr && ticket.date <= toStr;
      });
    } else if (dateMode === 'month' && selectedMonthFilter) {
      filtered = filtered.filter(ticket => {
        if (!ticket.date) return false;
        return ticket.date.substring(0, 7) === format(selectedMonthFilter, 'yyyy-MM');
      });
    }

    if (selectedClub && selectedClub !== 'ყველა კლუბი') {
      filtered = filtered.filter(ticket =>
        ticket.homeTeam === selectedClub || ticket.awayTeam === selectedClub
      );
    }

    setFilteredTickets(filtered);
  }, [tickets, selectedDate, dateFrom, dateTo, selectedMonthFilter, dateMode, selectedClub, activeTab]);

  const loadMoreTickets = useCallback(async () => {
    // Since we're only using real admin-created matches, disable infinite scroll
    setHasMore(false);
  }, []);

  const handleDateSelect = (date) => {
    if (calendarTarget === 'from') {
      setDateFrom(date);
      if (dateTo && date > dateTo) setDateTo(null);
      setCalendarTarget('to');
      // keep calendar open to pick 'to'
    } else if (calendarTarget === 'to') {
      if (dateFrom && date < dateFrom) {
        setDateFrom(date);
        setDateTo(dateFrom);
      } else {
        setDateTo(date);
      }
      setShowCalendar(false);
    } else {
      setSelectedDate(date);
      setShowCalendar(false);
    }
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedMonthFilter(null);
  };

  const isInRange = (date) => {
    if (dateMode !== 'range' || !dateFrom || !dateTo) return false;
    const d = format(date, 'yyyy-MM-dd');
    return d > format(dateFrom, 'yyyy-MM-dd') && d < format(dateTo, 'yyyy-MM-dd');
  };

  const hasActiveFilter =
    (dateMode === 'single' && selectedDate) ||
    (dateMode === 'range' && (dateFrom || dateTo)) ||
    (dateMode === 'month' && selectedMonthFilter);

  const handleDateModeChange = (mode) => {
    setDateMode(mode);
    setSelectedDate(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedMonthFilter(null);
    setShowCalendar(false);
    setCalendarTarget('single');
  };

  const handleCalendarBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowCalendar(false);
    }
  };

  // Handle escape key to close calendar and dropdown
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (showCalendar) setShowCalendar(false);
        if (showClubDropdown) setShowClubDropdown(false);
      }
    };

    const handleClickOutside = (e) => {
      if (showClubDropdown && !e.target.closest('.club-filter-wrapper')) {
        setShowClubDropdown(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    } else {
      document.body.style.overflow = 'unset';
    }

    if (showClubDropdown) {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [showCalendar, showClubDropdown]);

  const getDaysInMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return eachDayOfInterval({ start, end });
  };

  const hasTicketsOnDate = (date) => {
    return tickets.some(ticket => {
      if (!ticket.date) return false;
      const ticketDate = ticket.date; // Format: 'YYYY-MM-DD'
      const checkDate = format(date, 'yyyy-MM-dd');
      return ticketDate === checkDate;
    });
  };

  if (loading) {
    return (
      <div className="game-history">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">იტვირთება თამაშების ისტორია...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="game-history"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <motion.h1
            className="page-title"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            თამაშების ისტორია 📊
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            ყველა წინა პროგნოზი და შედეგი
          </motion.p>
        </div>

        {/* History Tabs */}
        <motion.div
          className="history-tabs"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '2rem' }}
        >
          <button
            className={`history-tab ${activeTab === 'high-risk' ? 'active' : ''}`}
            onClick={() => setActiveTab('high-risk')}
            style={{
              padding: '10px 20px',
              borderRadius: '20px',
              border: 'none',
              background: activeTab === 'high-risk' ? 'linear-gradient(135deg, #ff6b35, #ff4757)' : '#2a2a2a',
              color: activeTab === 'high-risk' ? 'white' : '#888',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.3s ease'
            }}
          >
            მაღალი რისკი
          </button>
          <button
            className={`history-tab ${activeTab === 'regular' ? 'active' : ''}`}
            onClick={() => setActiveTab('regular')}
            style={{
              padding: '10px 20px',
              borderRadius: '20px',
              border: 'none',
              background: activeTab === 'regular' ? 'linear-gradient(135deg, #ff6b35, #ff4757)' : '#2a2a2a',
              color: activeTab === 'regular' ? 'white' : '#888',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.3s ease'
            }}
          >
            ჩვეულებრივი
          </button>
          <button
            className={`history-tab ${activeTab === 'live' ? 'active' : ''}`}
            onClick={() => setActiveTab('live')}
            style={{
              padding: '10px 20px',
              borderRadius: '20px',
              border: 'none',
              background: activeTab === 'live' ? 'linear-gradient(135deg, #ff6b35, #ff4757)' : '#2a2a2a',
              color: activeTab === 'live' ? 'white' : '#888',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.3s ease'
            }}
          >
            LIVE ისტორია
          </button>
        </motion.div>

        {/* Filters */}
        <motion.div
          className="filters-container-modern"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {/* Date Filter */}
          <div className="filter-card date-filter-card">
            <label className="filter-label-modern">📅 თარიღი</label>

            {/* Date mode selector */}
            <div className="date-mode-selector">
              <button
                className={`date-mode-btn ${dateMode === 'single' ? 'active' : ''}`}
                onClick={() => handleDateModeChange('single')}
              >
                📅 ერთი დღე
              </button>
              <button
                className={`date-mode-btn ${dateMode === 'range' ? 'active' : ''}`}
                onClick={() => handleDateModeChange('range')}
              >
                📆 პერიოდი
              </button>
              <button
                className={`date-mode-btn ${dateMode === 'month' ? 'active' : ''}`}
                onClick={() => handleDateModeChange('month')}
              >
                🗓️ მთელი თვე
              </button>
            </div>

            {/* Single date mode */}
            {dateMode === 'single' && (
              <div className="date-filter">
                <button
                  className="date-selector-modern"
                  onClick={() => { setCalendarTarget('single'); setShowCalendar(!showCalendar); }}
                >
                  <span>{selectedDate ? formatGeorgianDate(selectedDate) : 'აირჩიეთ თარიღი'}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {selectedDate && (
                  <button className="clear-button" onClick={clearDateFilter} title="გასუფთავება">✕</button>
                )}
              </div>
            )}

            {/* Range mode */}
            {dateMode === 'range' && (
              <div className="date-range-container">
                <div className="date-range-row">
                  <div className="date-range-field">
                    <span className="date-range-label">დასაწყისი</span>
                    <button
                      className={`date-range-btn ${calendarTarget === 'from' && showCalendar ? 'active-target' : ''}`}
                      onClick={() => { setCalendarTarget('from'); setShowCalendar(true); }}
                    >
                      {dateFrom ? formatGeorgianDate(dateFrom) : '-- / -- / ----'}
                    </button>
                  </div>
                  <span className="date-range-arrow">→</span>
                  <div className="date-range-field">
                    <span className="date-range-label">დასასრული</span>
                    <button
                      className={`date-range-btn ${calendarTarget === 'to' && showCalendar ? 'active-target' : ''}`}
                      onClick={() => { setCalendarTarget('to'); setShowCalendar(true); }}
                    >
                      {dateTo ? formatGeorgianDate(dateTo) : '-- / -- / ----'}
                    </button>
                  </div>
                  {(dateFrom || dateTo) && (
                    <button className="clear-button" onClick={clearDateFilter} title="გასუფთავება">✕</button>
                  )}
                </div>
                {showCalendar && dateMode === 'range' && (
                  <p className="range-hint">
                    {calendarTarget === 'from' ? '👆 აირჩიეთ საწყისი თარიღი' : '👆 აირჩიეთ საბოლოო თარიღი'}
                  </p>
                )}
              </div>
            )}

            {/* Month mode */}
            {dateMode === 'month' && (
              <div className="month-filter-container">
                <div className="month-nav">
                  <button
                    className="nav-button-modern"
                    onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() - 1))}
                  >‹</button>
                  <span className="month-nav-label">
                    {georgianMonths[monthNavDate.getMonth()]} {monthNavDate.getFullYear()}
                  </span>
                  <button
                    className="nav-button-modern"
                    onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() + 1))}
                  >›</button>
                </div>
                <div className="month-action-row">
                  <button
                    className={`select-month-btn ${selectedMonthFilter && format(selectedMonthFilter, 'yyyy-MM') === format(monthNavDate, 'yyyy-MM') ? 'selected' : ''}`}
                    onClick={() => setSelectedMonthFilter(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth(), 1))}
                  >
                    {selectedMonthFilter && format(selectedMonthFilter, 'yyyy-MM') === format(monthNavDate, 'yyyy-MM')
                      ? '✓ შერჩეულია'
                      : 'ამ თვის არჩევა'}
                  </button>
                  {selectedMonthFilter && (
                    <button className="clear-button" onClick={clearDateFilter} title="გასუფთავება">✕</button>
                  )}
                </div>
              </div>
            )}

            {/* Shared calendar modal (single + range modes) */}
            {showCalendar && dateMode !== 'month' && (
              <div className="calendar-modal" onClick={handleCalendarBackdropClick}>
                <motion.div
                  className="calendar-content-modern"
                  onClick={(e) => e.stopPropagation()}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="calendar-header-modern">
                    <button
                      className="nav-button-modern"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      aria-label="წინა თვე"
                    >‹</button>
                    <span className="month-year-modern">
                      {georgianMonths[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </span>
                    <button
                      className="nav-button-modern"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      aria-label="შემდეგი თვე"
                    >›</button>
                    <button
                      className="close-calendar-modern"
                      onClick={() => setShowCalendar(false)}
                      aria-label="დახურვა"
                    >✕</button>
                  </div>
                  <div className="weekdays-row">
                    {georgianWeekdays.map((day, idx) => (
                      <div key={idx} className="weekday-label">{day}</div>
                    ))}
                  </div>
                  <div className="calendar-grid-modern">
                    {getDaysInMonth(currentMonth).map(date => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const isSelected =
                        (dateMode === 'single' && selectedDate && dateStr === format(selectedDate, 'yyyy-MM-dd')) ||
                        (dateMode === 'range' && ((dateFrom && dateStr === format(dateFrom, 'yyyy-MM-dd')) || (dateTo && dateStr === format(dateTo, 'yyyy-MM-dd'))));
                      const inRange = isInRange(date);
                      return (
                        <button
                          key={date.toISOString()}
                          className={`calendar-day-modern ${isSelected ? 'selected' : ''} ${inRange ? 'in-range' : ''} ${hasTicketsOnDate(date) ? 'has-tickets' : ''}`}
                          onClick={() => handleDateSelect(date)}
                          disabled={!hasTicketsOnDate(date)}
                          aria-label={`${format(date, 'd')} ${georgianMonths[date.getMonth()]}`}
                        >
                          {format(date, 'd')}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            )}
          </div>

          {/* Club Dropdown Filter with integrated search */}
          <div className="filter-card club-filter-wrapper">
            <label className="filter-label-modern">🔍 ძიება / კლუბი</label>
            <div className="club-dropdown-container">
              <button
                className="club-dropdown-button"
                onClick={() => setShowClubDropdown(!showClubDropdown)}
              >
                <span>{selectedClub || 'ყველა კლუბი'}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {selectedClub && selectedClub !== 'ყველა კლუბი' && (
                <button
                  className="clear-button"
                  onClick={() => setSelectedClub('ყველა კლუბი')}
                  title="გასუფთავება"
                >
                  ✕
                </button>
              )}

              {showClubDropdown && (
                <motion.div
                  className="club-dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="club-search-wrapper">
                    <input
                      type="text"
                      className="club-search-input"
                      placeholder="🔎 მოძებნე კლუბი..."
                      value={clubSearchTerm}
                      onChange={(e) => setClubSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className="club-options-list">
                    {filteredClubs.length > 0 ? (
                      filteredClubs.map(club => (
                        <button
                          key={club}
                          className={`club-option ${selectedClub === club ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedClub(club);
                            setShowClubDropdown(false);
                            setClubSearchTerm('');
                          }}
                        >
                          {club === 'ყველა კლუბი' ? '⚽ ' : '👉 '}  {club}
                        </button>
                      ))
                    ) : (
                      <div className="no-results">კლუბი ვერ მოიძებნა</div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Results */}
        <motion.div
          className="results-container"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {filteredTickets.length > 0 ? (
            <InfiniteScroll
              dataLength={filteredTickets.length}
              next={loadMoreTickets}
              hasMore={hasMore && !hasActiveFilter && selectedClub === ''}
              loader={
                <div className="load-more-container">
                  <div className="spinner"></div>
                  <p>იტვირთება მეტი...</p>
                </div>
              }
              endMessage={
                <div className="end-message">
                  <p>🎉 ყველა პროგნოზი ნაჩვენებია!</p>
                </div>
              }
            >
              <div className="tickets-grid">
                <div className="tc-header">
                  <span className="tc-header-label">კლუბები</span>
                  <span className="tc-header-label">პროგნოზი</span>
                  <span className="tc-header-label">კოეფ. | თარიღი</span>
                </div>
                {filteredTickets.map((ticket, index) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    showResult={false}
                    index={index}
                    isUpdating={updatingCards.has(ticket.id)}
                  />
                ))}
              </div>
            </InfiniteScroll>
          ) : (
            <div className="empty-container">
              <div className="empty-icon">📊</div>
              <h3 className="empty-title">პროგნოზები ვერ მოიძებნა</h3>
              <p className="empty-message">
                {hasActiveFilter || selectedClub !== 'ყველა კლუბი'
                  ? 'შერჩეული ფილტრებით პროგნოზები ვერ მოიძებნა. სცადეთ სხვა ფილტრები.'
                  : 'ჯერ არ არის დამატებული პროგნოზები.'
                }
              </p>
              {(hasActiveFilter || selectedClub !== 'ყველა კლუბი') && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setSelectedDate(null);
                    setSelectedClub('ყველა კლუბი');
                  }}
                >
                  ფილტრების გასუფთავება
                </button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default GameHistory;